/**
 * Main Application Component
 * @implements TS-032 - Customer Portal Frontend Package Setup
 * @implements TS-012 - Dev Mode Indicator
 *
 * Sets up the application with all required providers:
 * - ApolloProvider for GraphQL
 * - ThemeProvider for Material-UI
 * - AuthProvider for authentication
 * - RouterProvider for routing
 */

import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { RouterProvider } from 'react-router-dom';

import { apolloClient } from '@/apollo';
import { theme } from '@/theme';
import { AuthProvider } from '@/providers';
import { router } from '@/routes';
import { DevModeIndicator } from '@/components/dev';

/**
 * App component
 *
 * Provider hierarchy:
 * 1. ApolloProvider - GraphQL client for all data fetching
 * 2. ThemeProvider - Material-UI theming
 * 3. CssBaseline - CSS reset and normalization
 * 4. AuthProvider - Authentication state management
 * 5. RouterProvider - React Router for navigation
 * 6. DevModeIndicator - Development mode UI (only in DEV)
 *
 * @implements TS-012.2 - Add DevModeIndicator to App.tsx
 */
function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <RouterProvider router={router} />
          <DevModeIndicator />
        </AuthProvider>
      </ThemeProvider>
    </ApolloProvider>
  );
}

export default App;
